import { apiClient } from '../utils/api';
import type {
  CampaignData,
  AdCreativeData,
  ImpressionLimits,
  NegativeBrand,
  SceneTargeting,
  ContentTypeTargeting,
  SpecificContentTargeting,
  AdDisplayType,
  CampaignListResponse,
  CampaignResponse,
  CampaignAnalyticsResponse
} from '../types/campaign';

// Campaign API endpoints
export const campaignApi = {
  // List campaigns with enhanced filtering
  list: async (params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    status?: string;
    campaignType?: string;
    displayType?: AdDisplayType;
    search?: string;
    startDateFrom?: string;
    startDateTo?: string;
    budgetMin?: number;
    budgetMax?: number;
    hasImpressionLimits?: boolean;
    includeMetrics?: boolean;
  }): Promise<CampaignListResponse> => {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = `/campaigns${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<CampaignListResponse>(endpoint);
  },

  // Get single campaign
  get: async (id: string): Promise<CampaignResponse> => {
    return apiClient.get<CampaignResponse>(`/campaigns/${id}`);
  },

  // Create campaign
  create: async (data: CampaignCreateData): Promise<CampaignResponse> => {
    return apiClient.post<CampaignResponse>('/campaigns', data);
  },

  // Update campaign
  update: async (id: string, data: Partial<CampaignCreateData>): Promise<CampaignResponse> => {
    return apiClient.put<CampaignResponse>(`/campaigns/${id}`, data);
  },

  // Delete campaign
  delete: async (id: string): Promise<void> => {
    return apiClient.delete(`/campaigns/${id}`);
  },

  // Toggle campaign status
  toggleStatus: async (id: string): Promise<CampaignResponse> => {
    return apiClient.post<CampaignResponse>(`/campaigns/${id}/toggle-status`);
  },

  // Validate campaign
  validate: async (id: string): Promise<any> => {
    return apiClient.post(`/campaigns/${id}/validate`);
  },

  // Get impression limits
  getImpressionLimits: async (id: string): Promise<any> => {
    return apiClient.get(`/campaigns/${id}/impression-limits`);
  },

  // Update impression limits
  updateImpressionLimits: async (id: string, limits: ImpressionLimits): Promise<any> => {
    return apiClient.put(`/campaigns/${id}/impression-limits`, limits);
  },

  // Get campaign analytics
  getAnalytics: async (id: string, params?: {
    startDate?: string;
    endDate?: string;
    granularity?: 'day' | 'hour';
    metrics?: string;
  }): Promise<CampaignAnalyticsResponse> => {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = `/campaigns/${id}/analytics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<CampaignAnalyticsResponse>(endpoint);
  },

  // Process campaign keywords against existing transcriptions
  processKeywords: async (id: string): Promise<any> => {
    return apiClient.post(`/campaigns/${id}/process-keywords`, {});
  },

  // Toggle creative status (active/paused)
  toggleCreativeStatus: async (id: string, creativeId: string, status: 'active' | 'paused'): Promise<CampaignResponse> => {
    return apiClient.put<CampaignResponse>(`/campaigns/${id}`, {
      creativeStatusUpdate: {
        creativeId,
        status
      }
    });
  }
};

// Ad Creative API endpoints
export const adCreativeApi = {
  // List ad creatives for a campaign
  list: async (campaignId: string) => {
    return apiClient.get(`/campaigns/${campaignId}/creatives`);
  },

  // Create ad creative
  create: async (campaignId: string, data: AdCreativeData) => {
    return apiClient.post(`/campaigns/${campaignId}/creatives`, data);
  },

  // Update ad creative
  update: async (campaignId: string, creativeId: string, data: Partial<AdCreativeData>) => {
    return apiClient.put(`/campaigns/${campaignId}/creatives/${creativeId}`, data);
  },

  // Delete ad creative
  delete: async (campaignId: string, creativeId: string) => {
    return apiClient.delete(`/campaigns/${campaignId}/creatives/${creativeId}`);
  }
};

// File upload API
export const fileApi = {
  // Get upload URL for ad creative assets
  getUploadUrl: async (fileName: string, fileType: string) => {
    return apiClient.post('/upload-url', { fileName, fileType });
  },

  // Upload file to S3
  uploadFile: async (uploadUrl: string, file: File) => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload file');
    }
    
    return response;
  }
};

// Content API for targeting and management
export const contentApi = {
  // List content with enhanced filtering
  list: async (params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    contentType?: string;
    status?: string;
    showArchived?: boolean;
    groupName?: string;
  }) => {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = `/content${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get(endpoint);
  },

  // Get single content item
  get: async (id: string) => {
    return apiClient.get(`/content/${id}`);
  },

  // Create content
  create: async (data: any) => {
    return apiClient.post('/content', data);
  },

  // Update content
  update: async (id: string, data: any) => {
    return apiClient.put(`/content/${id}`, data);
  },

  // Delete content
  delete: async (id: string) => {
    return apiClient.delete(`/content/${id}`);
  },

  // Archive content (soft delete)
  archive: async (id: string) => {
    return apiClient.post(`/content/${id}/archive`);
  },

  // Restore archived content
  restore: async (id: string) => {
    return apiClient.post(`/content/${id}/restore`);
  },

  // Retry transcription for content
  retryTranscription: async (id: string) => {
    return apiClient.post(`/content/${id}/retry-transcription`);
  },

  // Get upload URL for content media
  getUploadUrl: async (id: string, fileName: string, fileType: string) => {
    return apiClient.post(`/content/${id}/upload-url`, { fileName, fileType });
  },

  // Get content keywords
  getKeywords: async (id: string) => {
    return apiClient.get(`/content/${id}/keywords`);
  },

  // Get content keywords with segment context
  getKeywordsWithContext: async (id: string) => {
    return apiClient.get(`/content/${id}/keywords-context`);
  },

  // Get content transcript segments
  getTranscriptSegments: async (id: string) => {
    return apiClient.get(`/content/${id}/segments`);
  },

  // Get content transcript in VTT format
  getTranscriptVTT: async (id: string) => {
    // Use a simple approach - get as regular response and extract text
    try {
      const response = await apiClient.get(`/content/${id}/transcript.vtt`);
      return response as string;
    } catch (error) {
      console.error('Failed to get VTT transcript:', error);
      throw error;
    }
  },

  // Search in transcript segments
  searchSegments: async (id: string, query: string, limit: number = 50, offset: number = 0) => {
    return apiClient.get(`/content/${id}/search?search=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
  },

  // Download transcript as VTT file
  downloadTranscriptVTT: async (id: string, title: string) => {
    try {
      const vttContent = await contentApi.getTranscriptVTT(id);
      
      // Create download link
      const blob = new Blob([vttContent], { type: 'text/vtt' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}-transcript.vtt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download VTT transcript:', error);
      throw error;
    }
  },

  // Upload manual transcript
  uploadTranscript: async (id: string, file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const fileContent = e.target?.result as string;
          const base64Content = btoa(fileContent);
          
          const response = await apiClient.post(`/content/${id}/upload-transcript`, {
            fileName: file.name,
            fileType: file.name.toLowerCase().endsWith('.vtt') ? 'vtt' : 'srt',
            fileContent: base64Content,
            fileSize: file.size
          });
          
          resolve(response);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  },

  // Start manual transcription
  startTranscription: async (id: string) => {
    return apiClient.post(`/content/${id}/start-transcription`);
  },

  // List all transcriptions for content
  listTranscriptions: async (id: string) => {
    return apiClient.get(`/content/${id}/transcriptions`);
  },

  // Set active transcription
  setActiveTranscription: async (id: string, transcriptionId: string) => {
    return apiClient.put(`/content/${id}/active-transcription`, { transcriptionId });
  },

  // Get all tenant groups
  getGroups: async (): Promise<string[]> => {
    return apiClient.get('/content/groups');
  }
};

// Global content search API
export const searchContent = async (params: {
  query: string;
  limit?: number;
  offset?: number;
}) => {
  const queryParams = new URLSearchParams();
  queryParams.append('q', params.query);
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.offset) queryParams.append('offset', params.offset.toString());
  
  const endpoint = `/content/search?${queryParams.toString()}`;
  return apiClient.get(endpoint);
};

// Enhanced campaign creation data interface
export interface CampaignCreateData extends CampaignData {
  targetKeywords?: string[];
  impressionLimits?: ImpressionLimits;
  negativeKeywords?: string[]; // Simplified for API
  negativeBrands?: NegativeBrand[];
  sceneTargeting?: SceneTargeting[];
  contentTypeTargeting?: ContentTypeTargeting[];
  specificContentTargeting?: SpecificContentTargeting[];
  adCreatives?: AdCreativeData[];
}

// Campaign with enhanced data
export interface EnhancedCampaign extends CampaignData {
  id: string;
  tenantId: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  impressionLimits?: any[];
  metrics?: {
    impressions: number;
    clicks: number;
    ctr: number;
  };
  validation?: {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  };
}

// Dashboard API endpoints
export const dashboardApi = {
  // Get dashboard overview stats
  getStats: async (filters?: any) => {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && value !== null && 'start' in value && 'end' in value) {
            // Handle date range
            queryParams.append(`${key}Start`, (value as any).start);
            queryParams.append(`${key}End`, (value as any).end);
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }
    
    const endpoint = `/dashboard/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get(endpoint);
  },

  // Get detailed analytics data
  getAnalytics: async (filters?: any) => {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && value !== null && 'start' in value && 'end' in value) {
            // Handle date range
            queryParams.append(`${key}Start`, (value as any).start);
            queryParams.append(`${key}End`, (value as any).end);
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }
    
    const endpoint = `/dashboard/analytics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get(endpoint);
  },

  // Get performance metrics for specific period
  getPerformanceMetrics: async (filters?: any) => {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && value !== null && 'start' in value && 'end' in value) {
            // Handle date range
            queryParams.append(`${key}Start`, (value as any).start);
            queryParams.append(`${key}End`, (value as any).end);
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }
    
    const endpoint = `/dashboard/performance${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get(endpoint);
  },

  // Get quick metrics for dashboard widgets
  getQuickMetrics: async () => {
    return apiClient.get('/dashboard/quick-metrics');
  },

  // Get recent activity feed
  getRecentActivity: async (limit: number = 10) => {
    return apiClient.get(`/dashboard/activity?limit=${limit}`);
  },

  // Get top performing campaigns
  getTopCampaigns: async (filters?: any) => {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = `/dashboard/top-campaigns${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get(endpoint);
  },

  // Get top performing content
  getTopContent: async (filters?: any) => {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = `/dashboard/top-content${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get(endpoint);
  },

  // Get campaign performance widgets
  getCampaignWidgets: async () => {
    return apiClient.get('/dashboard/campaign-widgets');
  }
};

export default {
  campaigns: campaignApi,
  adCreatives: adCreativeApi,
  files: fileApi,
  content: contentApi,
  dashboard: dashboardApi
};